import { useEffect, useRef, useState } from "react";
import { Lock } from "lucide-react";

import { toast } from "@/shared/hooks/use-toast";
import { errorToast } from "@/shared/lib/apiErrors";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/shared/ui/alert-dialog";
import { Button } from "@/shared/ui/button";

import { acqService, type AcqBatchUnlockProgress } from "../../api/acqService";

/** 一批最多几条(与后端 `AcqBatchUnlockService.MAX_BATCH` 同值)。 */
export const BATCH_UNLOCK_MAX = 50;
const POLL_MS = 2000;

/**
 * 线索库「批量补全联系方式」那一条(1015 企业库 P1)。
 *
 * ★ 钱的说法与单条一致:**查到才扣**;确认框里写「最多扣 N × 上沿」,不写一个必然的总价 ——
 *   多数情况下扣得比这个少(查不到的不扣、走公网那一档更便宜)。
 * ★ 跑完只报结果数,不在列表上逐条提示:列表重拉之后每条的号码就在那一行上。
 */
export function BatchUnlockBar({
  selectedIds,
  priceMin,
  priceMax,
  onClear,
  onDone,
}: {
  selectedIds: string[];
  priceMin: number | null;
  priceMax: number | null;
  onClear: () => void;
  /** 跑完(或中途停了)之后:重拉列表。 */
  onDone: () => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const [progress, setProgress] = useState<AcqBatchUnlockProgress | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const n = selectedIds.length;
  const running = progress != null && !progress.finished;
  const top = priceMax ?? priceMin;

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    []
  );

  const finish = (p: AcqBatchUnlockProgress) => {
    setProgress(null);
    onClear();
    onDone();
    const stopped = p.stoppedReason === "insufficient_credits";
    toast({
      title: stopped ? "积分不够了，批量补全已停下" : "批量补全完成",
      description:
        `查到 ${p.found} 条，没找到 ${p.notFound} 条` +
        (p.failed > 0 ? `，未处理 ${p.failed} 条` : "") +
        `；共扣 ${p.chargedCredits} 积分（没找到的不扣）`,
    });
  };

  const poll = (taskId: string) => {
    timer.current = setTimeout(async () => {
      try {
        const p = await acqService.batchUnlockProgress(taskId);
        if (p.finished) finish(p);
        else {
          setProgress(p);
          poll(taskId);
        }
      } catch (e) {
        setProgress(null);
        onDone();
        toast(errorToast(e, { fallbackTitle: "批量补全进度查不到了，请刷新列表查看结果" }));
      }
    }, POLL_MS);
  };

  const start = async () => {
    setConfirming(false);
    try {
      const p = await acqService.batchUnlock(selectedIds);
      if (p.finished) finish(p);
      else {
        setProgress(p);
        poll(p.taskId);
      }
    } catch (e) {
      toast(errorToast(e, { fallbackTitle: "批量补全没开始，积分未扣" }));
    }
  };

  if (n === 0 && !running) return null;

  return (
    <div
      className="flex flex-wrap items-center gap-2 rounded-xl border bg-card px-3 py-2"
      data-testid="acq-batch-unlock-bar"
    >
      <span className="text-sm">
        {running
          ? `正在补全 ${progress!.done}/${progress!.total}，已查到 ${progress!.found} 条`
          : `已选 ${n} 条`}
      </span>
      {!running && (
        <>
          <Button
            type="button"
            size="sm"
            disabled={n > BATCH_UNLOCK_MAX}
            onClick={() => setConfirming(true)}
          >
            <Lock className="h-4 w-4" />
            批量补全联系方式
          </Button>
          <Button type="button" size="sm" variant="ghost" onClick={onClear}>
            取消选择
          </Button>
          {n > BATCH_UNLOCK_MAX && (
            <span className="text-2xs text-destructive">
              一次最多 {BATCH_UNLOCK_MAX} 条，请少选一些
            </span>
          )}
        </>
      )}

      <AlertDialog open={confirming} onOpenChange={setConfirming}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>补全 {n} 条线索的联系方式？</AlertDialogTitle>
            <AlertDialogDescription>
              {top == null
                ? "按条计费，查到才扣，没找到的不扣。"
                : `查到才扣，没找到的不扣；每条 ${
                    priceMax != null && priceMin != null && priceMax > priceMin
                      ? `${priceMin}–${priceMax}`
                      : top
                  } 积分，最多扣 ${n * top} 积分。`}
              已经有电话的线索不会重复扣。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>再想想</AlertDialogCancel>
            <AlertDialogAction onClick={() => void start()}>开始补全</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
